/**
 * Sentry React integration for the browser SPA.
 *
 * Reads DSN + env from Vite-injected env vars (must be prefixed with
 * `VITE_` so Vite emits them to the client bundle). If
 * `VITE_SENTRY_DSN` is empty, Sentry is disabled and all exports are
 * silent no-ops.
 *
 * Render's Static Site / Web Service build env sets these at deploy
 * time — see render.yaml.
 *
 * Tagging:
 *   - release    (VITE_SENTRY_RELEASE, set by deploy hook to git SHA)
 *   - environment (VITE_SENTRY_ENVIRONMENT, e.g. "production")
 *   - user_id     (attached after login by AuthProvider)
 */
import * as Sentry from "@sentry/react";

let initialized = false;

export function initClientSentry() {
    const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
    if (!dsn) return;

    Sentry.init({
        dsn,
        environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || "production",
        release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
        // Traces sample very low by default — bump in Sentry UI if needed.
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.05),
        integrations: [
            Sentry.browserTracingIntegration(),
            // Session Replay: deliberately disabled by default (privacy +
            // bundle size). Enable via env when actively debugging.
        ],
        // Sentry v11 collects MORE by default when this is left unset
        // (cookies, request/response bodies, auto user info). Pin the v10
        // baseline from Sentry's migration guide so the upgrade does not
        // quietly start sending customer data from the browser. The user
        // we set ourselves via setSentryUser() is still attached.
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
                response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
            },
            httpBodies: [],
            urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            queues: false,
            graphQL: { document: false, variables: false },
        },
        beforeSend(event) {
            // Strip PII just in case beforeSend defaults change. We never
            // need the client's IP in error reports.
            if (event.user) {
                delete event.user.ip_address;
            }
            return event;
        },
    });
    initialized = true;
}

export function isClientSentryEnabled(): boolean {
    return initialized;
}

/** Update the active Sentry user — call from AuthProvider after sign-in. */
export function setSentryUser(user: { id: string; email?: string; roleName?: string } | null) {
    if (!initialized) return;
    if (!user) {
        Sentry.setUser(null);
        return;
    }
    Sentry.setUser({ id: user.id, email: user.email });
    Sentry.setTag("role", user.roleName ?? "unknown");
}

/** Wrap a React subtree with Sentry's error boundary. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/** Report a handled error (no-op when Sentry is disabled). */
export function captureClientError(err: unknown, context?: Record<string, unknown>) {
    if (!initialized) return;
    Sentry.captureException(err, context ? { extra: context } : undefined);
}
